import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    Trash2,
    Calendar,
    Percent,
    DollarSign,
    Users,
    Tag,
    AlertTriangle,
    Clock
} from 'lucide-react';
import { billingApi, Coupon } from '../../lib/api/billing';
import { productsApi, Product } from '../../lib/api/products';

export const DiscountDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;

    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Data Loading
    const [plans, setPlans] = useState<Product[]>([]);
    useEffect(() => {
        productsApi.list().then(setPlans).catch(console.error);
    }, []);

    // Form State
    const [code, setCode] = useState('');
    const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
    const [discountValue, setDiscountValue] = useState<number>(0);
    const [duration, setDuration] = useState<'once' | 'forever' | 'repeating'>('once');
    const [durationInMonths, setDurationInMonths] = useState<number | undefined>(undefined);

    const [appliesTo, setAppliesTo] = useState<'all' | 'specific'>('all');
    const [selectedPlans, setSelectedPlans] = useState<string[]>([]);

    // Usage Limits
    const [usageLimitTotal, setUsageLimitTotal] = useState<boolean>(false);
    const [totalLimit, setTotalLimit] = useState<number | undefined>(undefined);
    const [limitOnePerUser, setLimitOnePerUser] = useState<boolean>(false);

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [startTime, setStartTime] = useState('00:00');
    const [hasEndDate, setHasEndDate] = useState(false);
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('00:00');
    const [isActive, setIsActive] = useState(true);

    // Load Existing Coupon
    useEffect(() => {
        if (id) {
            const loadCoupon = async () => {
                try {
                    setIsLoading(true);
                    const coupon = await billingApi.getCoupon(id);

                    setCode(coupon.code);
                    setIsActive(coupon.isActive);

                    if (coupon.percentOff) {
                        setDiscountType('percent');
                        setDiscountValue(coupon.percentOff);
                    } else if (coupon.amountOff) {
                        setDiscountType('fixed');
                        setDiscountValue(coupon.amountOff / 100);
                    }

                    if (coupon.appliesToPlans && coupon.appliesToPlans.length > 0) {
                        setAppliesTo('specific');
                        setSelectedPlans(coupon.appliesToPlans);
                    } else {
                        setAppliesTo('all');
                    }

                    if (coupon.maxRedemptions) {
                        setUsageLimitTotal(true);
                        setTotalLimit(coupon.maxRedemptions);
                    }

                    if (coupon.maxRedemptionsPerUser) {
                        setLimitOnePerUser(true);
                    }

                    if (coupon.startsAt) {
                        const start = new Date(coupon.startsAt);
                        setStartDate(`${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`);
                        setStartTime(`${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`);
                    }

                    if (coupon.endsAt) {
                        setHasEndDate(true);
                        const end = new Date(coupon.endsAt);
                        setEndDate(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`);
                        setEndTime(`${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`);
                    }

                    if (coupon.duration) {
                        setDuration(coupon.duration);
                    }
                    if (coupon.durationInMonths) {
                        setDurationInMonths(coupon.durationInMonths);
                    }

                } catch (error) {
                    console.error('Failed to load coupon', error);
                    alert('Failed to load coupon details');
                    navigate('/payments/discounts');
                } finally {
                    setIsLoading(false);
                }
            };
            loadCoupon();
        }
    }, [id, navigate]);

    // -- Generate Code --
    const generateCode = () => {
        const random = Math.random().toString(36).substring(2, 10).toUpperCase();
        setCode(random);
    };

    // -- Submit --
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormErrors({});
        setIsLoading(true);

        try {
            // Validation
            if (!code.trim()) throw { code: 'Discount code is required' };
            if (discountValue <= 0) throw { value: 'Value must be greater than 0' };
            if (appliesTo === 'specific' && selectedPlans.length === 0) throw { plans: 'Select at least one product' };
            if (hasEndDate && !endDate) throw { date: 'End date is required' };

            // Start Date Construction
            const startDateTime = new Date(`${startDate}T${startTime}`);
            let endDateTime = null;
            if (hasEndDate && endDate) {
                endDateTime = new Date(`${endDate}T${endTime}`);
                if (endDateTime <= startDateTime) throw { date: 'End date must be after start date' };
            }

            // Payload Construction
            // Validation for repeating duration
            if (duration === 'repeating' && (!durationInMonths || durationInMonths < 1)) {
                throw { duration: 'Duration in months is required for repeating discounts' };
            }

            const payload: Partial<Coupon> = {
                code: code.toUpperCase(),
                name: code.toUpperCase(),
                isActive: isActive,
                duration,
                durationInMonths: duration === 'repeating' ? durationInMonths : undefined,

                startsAt: startDateTime.toISOString(),
                endsAt: endDateTime ? endDateTime.toISOString() : null,

                appliesToPlans: appliesTo === 'specific' ? selectedPlans : [],

                maxRedemptions: usageLimitTotal ? totalLimit : null,
                maxRedemptionsPerUser: limitOnePerUser ? 1 : null,
            };

            if (discountType === 'percent') {
                payload.percentOff = discountValue;
                payload.currency = 'USD'; // Default
            } else {
                payload.amountOff = Math.round(discountValue * 100);
                payload.currency = 'USD';
            }

            if (isEditMode && id) {
                await billingApi.updateCoupon(id, payload);
            } else {
                await billingApi.createCoupon(payload);
            }
            navigate('/payments/discounts');

        } catch (error: any) {
            console.error(error);
            if (error.code || error.value || error.plans || error.date) {
                setFormErrors(error);
            } else {
                alert('Failed to save discount');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!id || !window.confirm('Are you sure you want to delete this coupon?')) return;
        try {
            await billingApi.deleteCoupon(id);
            navigate('/payments/discounts');
        } catch (error) {
            alert('Failed to delete coupon');
        }
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-5xl mx-auto w-full pb-20">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/payments/discounts')}
                        className="btn-ghost !p-1.5"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div className="flex items-center gap-3">
                        <h1 className="text-xl font-bold text-neutral-900">
                            {isEditMode ? (code || 'Edit Discount') : 'New Discount'}
                        </h1>
                        {isEditMode && (
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${isActive
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-neutral-100 text-neutral-600 border-neutral-200'
                                }`}>
                                {isActive ? 'Active' : 'Archived'}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isEditMode && (
                        <button
                            onClick={handleDelete}
                            className="btn-secondary !px-3 !py-1.5 !text-xs !text-red-600 hover:!bg-red-50 hover:!border-red-200 flex items-center gap-1.5"
                        >
                            <Trash2 size={12} />
                            Delete
                        </button>
                    )}
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="btn-primary !px-3 !py-1.5 !text-xs flex items-center gap-1.5"
                    >
                        <Save size={12} />
                        {isLoading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">

                    {/* 1. Amount / Code */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                            <h2 className="text-sm font-semibold text-neutral-900">Amount & Code</h2>
                            <button onClick={generateCode} className="text-xs font-medium text-accent-primary hover:text-blue-700 hover:underline">Generate random code</button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Discount code</label>
                                <input
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded-md text-lg font-medium uppercase tracking-wide placeholder:text-neutral-300 ${formErrors.code ? 'border-red-500 focus:ring-red-200' : 'border-neutral-200 focus:border-accent-primary focus:ring-1 focus:ring-accent-primary'}`}
                                    placeholder="e.g. SUMMERCAKE20"
                                    value={code}
                                    onChange={e => setCode(e.target.value.toUpperCase())}
                                />
                                {formErrors.code && <p className="text-xs text-red-500 mt-1">{formErrors.code}</p>}
                                <p className="text-xs text-neutral-500 mt-1">Customers will enter this code at checkout.</p>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                                    <div className="relative">
                                        <select
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-md appearance-none bg-white focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                            value={discountType}
                                            onChange={(e) => setDiscountType(e.target.value as any)}
                                        >
                                            <option value="percent">Percentage</option>
                                            <option value="fixed">Fixed amount</option>
                                        </select>
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <span className="w-4 h-4 text-neutral-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_eb7d69bc.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_eb7d69bc.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Value</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                            value={discountValue || ''}
                                            onChange={e => setDiscountValue(parseFloat(e.target.value))}
                                            placeholder="0"
                                        />
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400 font-medium">
                                            {discountType === 'percent' ? '%' : '$'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t border-neutral-100 mt-4">
                                <div>
                                    <div className="text-sm font-medium text-neutral-900">Active</div>
                                    <div className="text-xs text-neutral-500">Discount is redeemable</div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={isActive}
                                        onChange={e => setIsActive(e.target.checked)}
                                    />
                                    <div className="w-9 h-5 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* 1.5. Duration */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Duration</h2>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">How long does the discount apply?</label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-3 p-3 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="duration"
                                            className="w-4 h-4 text-accent-primary focus:ring-accent-primary border-neutral-300"
                                            checked={duration === 'once'}
                                            onChange={() => setDuration('once')}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-neutral-900">Once</span>
                                            <p className="text-xs text-neutral-500">Discount applies to the first payment only</p>
                                        </div>
                                    </label>
                                    <label className="flex items-center gap-3 p-3 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="duration"
                                            className="w-4 h-4 text-accent-primary focus:ring-accent-primary border-neutral-300"
                                            checked={duration === 'repeating'}
                                            onChange={() => setDuration('repeating')}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-neutral-900">Multiple months</span>
                                            <p className="text-xs text-neutral-500">Discount applies for a set number of months</p>
                                        </div>
                                    </label>
                                    <label className="flex items-center gap-3 p-3 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50 transition-colors">
                                        <input
                                            type="radio"
                                            name="duration"
                                            className="w-4 h-4 text-accent-primary focus:ring-accent-primary border-neutral-300"
                                            checked={duration === 'forever'}
                                            onChange={() => setDuration('forever')}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-neutral-900">Forever</span>
                                            <p className="text-xs text-neutral-500">Discount applies to every future payment</p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {duration === 'repeating' && (
                                <div className="pl-7 animate-fade-in-down">
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Number of months</label>
                                    <input
                                        type="number"
                                        className={`w-32 px-3 py-2 border rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary ${formErrors.duration ? 'border-red-500' : 'border-neutral-200'}`}
                                        value={durationInMonths || ''}
                                        onChange={e => setDurationInMonths(parseInt(e.target.value))}
                                        placeholder="e.g. 3"
                                        min={1}
                                    />
                                    {formErrors.duration && <p className="text-xs text-red-500 mt-1">{formErrors.duration}</p>}
                                </div>
                            )}

                            {duration === 'forever' && (
                                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md animate-fade-in-down">
                                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold text-amber-800">Permanent discount</div>
                                        <p className="text-xs text-amber-700 mt-0.5">
                                            This discount will apply to <strong>every future payment</strong> for the lifetime of the subscription.
                                            This cannot be changed after creation in Stripe. Consider using "Once" or "Multiple months" for time-limited promotions.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 2. Applies To */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Applies To</h2>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="space-y-2">
                                <label className="flex items-center gap-3 p-3 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50 transition-colors">
                                    <input
                                        type="radio"
                                        name="appliesTo"
                                        className="w-4 h-4 text-accent-primary focus:ring-accent-primary border-neutral-300"
                                        checked={appliesTo === 'all'}
                                        onChange={() => setAppliesTo('all')}
                                    />
                                    <span className="text-sm font-medium text-neutral-900">All products</span>
                                </label>
                                <label className="flex items-center gap-3 p-3 border border-neutral-200 rounded-md cursor-pointer hover:bg-neutral-50 transition-colors">
                                    <input
                                        type="radio"
                                        name="appliesTo"
                                        className="w-4 h-4 text-accent-primary focus:ring-accent-primary border-neutral-300"
                                        checked={appliesTo === 'specific'}
                                        onChange={() => setAppliesTo('specific')}
                                    />
                                    <span className="text-sm font-medium text-neutral-900">Specific products</span>
                                </label>
                            </div>

                            {appliesTo === 'specific' && (
                                <div className="pl-7 animate-fade-in-down">
                                    <div className="mb-2 text-xs text-neutral-500">Select products this discount applies to:</div>
                                    <div className="space-y-2 max-h-48 overflow-y-auto border border-neutral-200 rounded-md p-2">
                                        {plans.map(plan => (
                                            <label key={plan.id} className="flex items-center gap-2 p-2 hover:bg-neutral-50 rounded cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-neutral-300 text-accent-primary focus:ring-accent-primary"
                                                    checked={selectedPlans.includes(plan.id)}
                                                    onChange={e => {
                                                        if (e.target.checked) setSelectedPlans([...selectedPlans, plan.id]);
                                                        else setSelectedPlans(selectedPlans.filter(pid => pid !== plan.id));
                                                    }}
                                                />
                                                <span className="text-sm text-neutral-700">{plan.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {formErrors.plans && <p className="text-xs text-red-500 mt-1">{formErrors.plans}</p>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Usage Limits */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Usage limits</h2>
                        </div>
                        <div className="p-4 space-y-4">
                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="rounded border-neutral-300 text-accent-primary focus:ring-accent-primary"
                                    checked={usageLimitTotal}
                                    onChange={e => setUsageLimitTotal(e.target.checked)}
                                />
                                <span className="text-sm text-neutral-900">Limit number of times this discount can be used in total</span>
                            </label>
                            {usageLimitTotal && (
                                <div className="pl-7">
                                    <input
                                        type="number"
                                        className="w-32 px-3 py-2 border border-neutral-200 rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                        value={totalLimit || ''}
                                        onChange={e => setTotalLimit(parseInt(e.target.value))}
                                        placeholder="e.g. 100"
                                    />
                                </div>
                            )}

                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="rounded border-neutral-300 text-accent-primary focus:ring-accent-primary"
                                    checked={limitOnePerUser}
                                    onChange={e => setLimitOnePerUser(e.target.checked)}
                                />
                                <span className="text-sm text-neutral-900">Limit to one use per customer</span>
                            </label>
                        </div>
                    </div>

                    {/* Active Dates */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Active Dates</h2>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Start date</label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                        value={startDate}
                                        onChange={e => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Start time</label>
                                    <input
                                        type="time"
                                        className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                        value={startTime}
                                        onChange={e => setStartTime(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        className="rounded border-neutral-300 text-accent-primary focus:ring-accent-primary"
                                        checked={hasEndDate}
                                        onChange={e => setHasEndDate(e.target.checked)}
                                    />
                                    <span className="text-sm font-medium text-neutral-900">Set end date</span>
                                </label>
                            </div>

                            {hasEndDate && (
                                <div className="grid grid-cols-2 gap-4 animate-fade-in-down">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 mb-1">End date</label>
                                        <input
                                            type="date"
                                            className={`w-full px-3 py-2 border rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary ${formErrors.date ? 'border-red-500' : 'border-neutral-200'}`}
                                            value={endDate}
                                            onChange={e => setEndDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 mb-1">End time</label>
                                        <input
                                            type="time"
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                            value={endTime}
                                            onChange={e => setEndTime(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}
                            {formErrors.date && <p className="text-xs text-red-500">{formErrors.date}</p>}
                        </div>
                    </div>
                </div>

                {/* Sidebar Summary */}
                <div className="space-y-6">
                    {/* Summary Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden sticky top-6">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">Summary</h2>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                                    <Tag size={14} className="text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-xs text-neutral-500">Discount Code</div>
                                    <div className="text-sm font-mono font-bold text-neutral-900 break-all">{code || '---'}</div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-neutral-100 space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 text-neutral-400">
                                        {discountType === 'percent' ? <Percent size={14} /> : <DollarSign size={14} />}
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-neutral-900">Value</div>
                                        <div className="text-xs text-neutral-500">
                                            {discountType === 'percent' ? `${discountValue}% off` : `$${discountValue} off`}
                                            {' '}
                                            {appliesTo === 'all' ? 'all products' : `on ${selectedPlans.length} specific products`}
                                        </div>
                                    </div>
                                </div>

                                {(usageLimitTotal || limitOnePerUser) && (
                                    <div className="flex items-start gap-3">
                                        <Users size={14} className="mt-0.5 text-neutral-400" />
                                        <div>
                                            <div className="text-xs font-medium text-neutral-900">Limits</div>
                                            <div className="text-xs text-neutral-500">
                                                {usageLimitTotal && <div>Total: {totalLimit} uses</div>}
                                                {limitOnePerUser && <div>One per customer</div>}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-start gap-3">
                                    <Clock size={14} className="mt-0.5 text-neutral-400" />
                                    <div>
                                        <div className="text-xs font-medium text-neutral-900">Duration</div>
                                        <div className="text-xs text-neutral-500">
                                            {duration === 'once' && 'First payment only'}
                                            {duration === 'forever' && <span className="text-amber-600 font-medium">Forever (all payments)</span>}
                                            {duration === 'repeating' && `${durationInMonths || '?'} months`}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <Calendar size={14} className="mt-0.5 text-neutral-400" />
                                    <div>
                                        <div className="text-xs font-medium text-neutral-900">Schedule</div>
                                        <div className="text-xs text-neutral-500">
                                            Starts {startDate} {startTime}
                                            {hasEndDate && <div>Ends {endDate} {endTime}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
